import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Fetchexecutequery,
  Fetchupdatequery,
} from "../../../services/process/BRS1/brsService7";
import { CreateData } from "../../../services/process/process_data/process_data";

import { headers, baseUrl } from "../../../services/common/credentials";
import {
  Building, Users, Globe, Paperclip, UserPlus, Check, Save, Upload,
  Link, Plus, Minus, AlertCircle, CheckCircle, ChevronDown, ChevronUp,
  BookOpen, Image, FileText, Video, Eye, School,
} from "lucide-react";

// ── Helpers ────────────────────────────────────────────────────────────────────
const getUserCredentials = () =>
  JSON.parse(sessionStorage.getItem("userCredentials") || "{}");

const getFormattedDate = () =>
  new Date().toISOString().slice(0, 19).replace("T", " ");

const FONT = "";

// ── Design tokens ──────────────────────────────────────────────────────────────
const T = {
  navy:        "#0a2352",
  navyDark:    "#061535",
  navyMid:     "#143272",
  orange:      "#f97316",
  orangeHover: "#ea6c0a",
  bg:          "#eef3f9",
  card:        "#ffffff",
  border:      "#d4dce8",
  text:        "#000000",
  muted:       "#000000",
  light:       "#e8eef5",
  success:     "#16a34a",
  danger:      "#dc2626",
  warn:        "#d97706",
};


// ── Constants ──────────────────────────────────────────────────────────────────
const STATIC_PATH   = "/home/demo/UserManagementModule/static/reference_files/";
const UPLOAD_URL    = `${baseUrl}/api/v2/user_modules/upload/`;
const DOWNLOAD_BASE = `${baseUrl}/api/v2/user_modules/serve-pdf/`;

const INDIAN_STATES = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh",
  "Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka",
  "Kerala","Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram",
  "Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana",
  "Tripura","Uttar Pradesh","Uttarakhand","West Bengal",
  // UTs
  "Andaman and Nicobar Islands","Chandigarh","Dadra and Nagar Haveli and Daman and Diu",
  "Delhi","Jammu and Kashmir","Ladakh","Lakshadweep","Puducherry",
];

const AFFILIATION_BOARDS = ["CBSE","ICSE","State Board","IB","Cambridge","Other"];
const SCHOOL_TYPES = ["Day School","Boarding School","Co-Ed","Boys","Girls"];
const STREAMS = ["Science","Commerce","Humanities","Vocational"];

// Logo upload config
const LOGO_CONFIG = {
    key: "logo",
    label: "School Logo *",
    hint: "PNG or JPG · Max 5 MB",
    accept: ".png,.jpg,.jpeg",
    maxMB: 5
};
const CAMPUS_CONFIG = { key: "campus",     label: "Campus Photos",          hint: "PNG/JPG · Multiple allowed · Max 10 MB each", accept: ".png,.jpg,.jpeg", maxMB: 10, multiple: true };
const BROCHURE_CONFIG = { key: "brochure", label: "Brochures / Flyers / PDFs", hint: "PDF, DOCX · Multiple allowed · Max 10 MB each", accept: ".pdf,.docx,.doc", maxMB: 10, multiple: true };


// ── Shared UI Atoms ────────────────────────────────────────────────────────────

const TopNav = ({ userCredentials }) => (
  <div style={{
    background: `linear-gradient(135deg, ${T.navyDark} 0%, ${T.navy} 60%, ${T.navyMid} 100%)`,
    padding: "0 32px", height: 56,
    display: "flex", alignItems: "center", justifyContent: "space-between",
    boxShadow: "0 2px 12px rgba(6,21,53,.45)",
    position: "sticky", top: 0, zIndex: 100,
  }}>
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <div style={{
        width: 32, height: 32, borderRadius: 8, background: T.orange,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <School size={18} color="#fff" />
      </div>
      <span style={{ color: "#fff", fontWeight: 800, fontSize: 16, letterSpacing: .2 }}>
        School Registration Portal
      </span>
    </div>
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{
        width: 34, height: 34, borderRadius: "50%", background: T.orange,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontWeight: 800, color: "#fff", fontSize: 14,
      }}>
        {(userCredentials?.display_name || userCredentials?.user_name || "U")[0].toUpperCase()}
      </div>
      <span style={{ color: "rgba(255,255,255,.85)", fontSize: 13, fontWeight: 600 }}>
        {userCredentials?.display_name || userCredentials?.user_name || "User"}
      </span>
    </div>
  </div>
);

const Card = ({ children, style = {} }) => (
  <div style={{
    background: T.card, borderRadius: 14,
    border: `1px solid ${T.border}`,
    boxShadow: "0 4px 20px rgba(10,35,82,.07)",
    overflow: "hidden", ...style,
  }}>
    {children}
  </div>
);

const SectionHeader = ({ icon: Icon, title, badge, isOpen, onToggle }) => (
  <div
    onClick={onToggle}
    style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "14px 20px",
      background: `linear-gradient(135deg, ${T.navy} 0%, ${T.navyMid} 100%)`,
      cursor: "pointer", userSelect: "none",
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{
        background: T.orange, borderRadius: 7, padding: "5px 6px",
        display: "flex", alignItems: "center",
      }}>
        <Icon size={17} color="#fff" />
      </div>
      <span style={{ color: "#fff", fontWeight: 800, fontSize: 18, letterSpacing: .2 }}>{title}</span>
      {badge != null && (
        <span style={{
          background: T.orange, color: "#fff",
          borderRadius: 99, padding: "1px 8px", fontSize: 11, fontWeight: 800,
        }}>{badge}</span>
      )}
    </div>
    {isOpen
      ? <ChevronUp size={18} color="rgba(255,255,255,.75)" />
      : <ChevronDown size={18} color="rgba(255,255,255,.75)" />}
  </div>
);

const SubHeader = ({ title }) => (
  <div style={{
    fontSize: 11, fontWeight: 800, color: T.navy,
    textTransform: "uppercase", letterSpacing: .6,
    marginBottom: 12, marginTop: 4,
    paddingBottom: 6, borderBottom: `2px solid ${T.light}`,
    fontFamily: FONT,
  }}>
    {title}
  </div>
);

const Field = ({ label, required, children, hint, fullWidth }) => (
  <div style={{ marginBottom: 14, gridColumn: fullWidth ? "1/-1" : undefined }}>
    <label style={{
      display: "block", fontSize: 16, fontWeight: 700, color: T.muted,
      marginBottom: 5, letterSpacing: .3,
    }}>
      {label}{required && <span style={{ color: T.danger, marginLeft: 3 }}>*</span>}
    </label>
    {children}
    {hint && <p style={{ fontSize: 11, color: T.muted, margin: "4px 0 0" }}>{hint}</p>}
  </div>
);

const inputBase = {
  width: "100%", padding: "9px 12px", borderRadius: 8,
  border: `1.5px solid ${T.border}`, fontSize: 15, color: T.text,
  background: "#fff", outline: "none", boxSizing: "border-box",
  transition: "border .18s, box-shadow .18s", fontFamily: FONT
};

const Input = ({ style = {}, ...props }) => {
  const [focused, setFocused] = useState(false);
  return (
    <input
      {...props}
      style={{
        ...inputBase,
        borderColor: focused ? T.orange : T.border,
        boxShadow: focused ? `0 0 0 3px rgba(249,115,22,.13)` : "none",
        ...style,
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    />
  );
};

const Select = ({ children, style = {}, ...props }) => {
  const [focused, setFocused] = useState(false);
  return (
    <select
      {...props}
      style={{
        ...inputBase,
        borderColor: focused ? T.orange : T.border,
        boxShadow: focused ? `0 0 0 3px rgba(249,115,22,.13)` : "none",
        ...style,
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    >
      {children}
    </select>
  );
};

const Textarea = ({ style = {}, maxLength, value, ...props }) => {
  const [focused, setFocused] = useState(false);
  // rough word count
  const wordCount = value ? value.trim().split(/\s+/).filter(Boolean).length : 0;
  return (
    <div>
      <textarea
        value={value}
        {...props}
        style={{
          ...inputBase,
          borderColor: focused ? T.orange : T.border,
          boxShadow: focused ? `0 0 0 3px rgba(249,115,22,.13)` : "none",
          resize: "vertical", minHeight: 90, ...style,
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      />
      {maxLength && (
        <p style={{
          fontSize: 11, margin: "3px 0 0", textAlign: "right",
          color: wordCount > maxLength ? T.danger : T.muted,
        }}>
          {wordCount} / {maxLength} words
        </p>
      )}
    </div>
  );
};

const Btn = ({ children, variant = "primary", size = "md", icon: Icon, onClick, disabled, style = {} }) => {
  const [hovered, setHovered] = useState(false);
  const pad = size === "sm" ? "7px 14px" : "10px 22px";
  const fs = size === "sm" ? 12 : 13;
  const variants = {
    primary: { background: hovered ? T.orangeHover : T.orange, color: "#fff", border: "none" },
    secondary: { background: hovered ? T.light : "#f1f5f9", color: T.navy, border: `1.5px solid ${T.border}` },
    navy: { background: hovered ? T.navyMid : T.navy, color: "#fff", border: "none" },
    success: { background: hovered ? "#15803d" : T.success, color: "#fff", border: "none" },
    outline: {
      background: "transparent", color: T.navy, border: `1.5px solid ${T.border}`,
      ...(hovered && { borderColor: T.orange, color: T.orange }),
    },
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        borderRadius: 8, fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer",
        padding: pad, fontSize: fs, transition: "all .15s",
        opacity: disabled ? .6 : 1, fontFamily: FONT,
        ...variants[variant], ...style,
      }}
    >
      {Icon && <Icon size={size === "sm" ? 13 : 14} />}
      {children}
    </button>
  );
};

// Checkbox
const Checkbox = ({ checked, onChange, label, required }) => (
  <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", marginBottom: 10 }}>
    <div
      onClick={() => onChange(!checked)}
      style={{
        width: 18, height: 18, borderRadius: 5, flexShrink: 0, marginTop: 1,
        border: `2px solid ${checked ? T.orange : T.border}`,
        background: checked ? T.orange : "#fff",
        display: "flex", alignItems: "center", justifyContent: "center",
        transition: "all .15s", cursor: "pointer",
      }}
    >
      {checked && <Check size={11} color="#fff" strokeWidth={3} />}
    </div>
    <span style={{ fontSize: 13, color: T.text, lineHeight: 1.5 }}>
      {label}
      {required && <span style={{ color: T.danger, marginLeft: 3 }}>*</span>}
    </span>
  </label>
);

// Multi-checkbox toggle for streams
const StreamToggle = ({ value, onChange }) => (
  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
    {STREAMS.map(s => {
      const active = value.includes(s);
      return (
        <button
          key={s}
          onClick={() => onChange(active ? value.filter(x => x !== s) : [...value, s])}
          style={{
            padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 700,
            border: `1.5px solid ${active ? T.orange : T.border}`,
            background: active ? "rgba(249,115,22,.1)" : "#fff",
            color: active ? T.orange : T.muted,
            cursor: "pointer", transition: "all .15s", fontFamily: FONT,
          }}
        >
          {s}
        </button>
      );
    })}
  </div>
);

// Toggle for international engagement
const ToggleSwitch = ({ value, onChange }) => (
  <div
    onClick={() => onChange(!value)}
    style={{
      width: 40, height: 22, borderRadius: 99, cursor: "pointer",
      background: value ? T.orange : T.border,
      position: "relative", transition: "background .2s", flexShrink: 0,
    }}
  >
    <div style={{
      position: "absolute", top: 3,
      left: value ? 20 : 3,
      width: 16, height: 16, borderRadius: "50%",
      background: "#fff", transition: "left .2s",
      boxShadow: "0 1px 4px rgba(0,0,0,.2)",
    }} />
  </div>
);

// Donut chart
const DonutChart = ({ pct, size = 88 }) => {
  const r = 36, cx = 44, cy = 44;
  const circ = 2 * Math.PI * r;
  const filled = (pct / 100) * circ;
  return (
    <svg width={size} height={size} viewBox="0 0 88 88">
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={T.light} strokeWidth={14} />
      <circle
        cx={cx} cy={cy} r={r} fill="none"
        stroke={pct > 80 ? T.success : pct > 50 ? T.orange : T.danger}
        strokeWidth={14}
        strokeDasharray={`${filled} ${circ - filled}`}
        style={{ transform: "rotate(-90deg)", transformOrigin: "center", transition: "stroke-dasharray .5s" }}
      />
      <text x="44" y="49" textAnchor="middle" fontSize="15" fontWeight="900" fill={T.navy}>{pct}%</text>
    </svg>
  );
};

const ProgressBar = ({ value, color = T.orange }) => (
  <div style={{ background: T.light, borderRadius: 99, height: 6, overflow: "hidden" }}>
    <div style={{
      width: `${Math.min(value, 100)}%`, height: "100%",
      background: color, borderRadius: 99, transition: "width .5s",
    }} />
  </div>
);

// ── Single File Upload Row ─────────────────────────────────────────────────────
const SingleFileRow = ({ config, uploaded, isUploading, onChoose, onClear }) => (
  <div style={{
    display: "flex", alignItems: "center", gap: 16, padding: "12px 16px",
    background: uploaded ? "rgba(249,115,22,.05)" : T.bg,
    borderRadius: 10,
    border: `1.5px dashed ${uploaded ? T.orange : T.border}`,
    fontFamily: FONT,
  }}>
    <div style={{
      background: uploaded ? "rgba(249,115,22,.15)" : T.light,
      borderRadius: 8, padding: 10, flexShrink: 0,
    }}>
      <Upload size={16} color={uploaded ? T.orange : T.navy} />
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontWeight: 700, fontSize: 13, color: T.text }}>{config.label}</div>
      {isUploading ? (
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3 }}>
          <div style={{
            width: 11, height: 11, border: `2px solid ${T.light}`,
            borderTop: `2px solid ${T.orange}`, borderRadius: "50%",
            animation: "spin 0.7s linear infinite",
          }} />
          <style>{`
  @keyframes spin { to { transform: rotate(360deg); } }
  * { box-sizing: border-box; }

  input::placeholder,
  textarea::placeholder {
    color: #94a3b8 !important;
    opacity: 1 !important;
  }
`}</style>
          <span style={{ fontSize: 12, color: T.orange, fontWeight: 600 }}>Uploading…</span>
        </div>
      ) : uploaded ? (
        <a href={`${DOWNLOAD_BASE}${uploaded.fileName}`} target="_blank" rel="noopener noreferrer"
          style={{
            fontSize: 12, color: "#007bff", textDecoration: "underline",
            display: "block", marginTop: 3, overflow: "hidden",
            textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
          {uploaded.fileName}
        </a>
      ) : (
        <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>{config.hint}</div>
      )}
    </div>
    {!isUploading && (
      uploaded ? (
        <button onClick={onClear} title="Remove file" style={{
          cursor: "pointer", background: "none", border: "none",
          color: T.danger, fontWeight: 800, fontSize: 15, lineHeight: 1,
          padding: "2px 7px", flexShrink: 0,
        }}>✕</button>
      ) : (
        <button onClick={onChoose} style={{
          background: "transparent", border: `1.5px solid ${T.border}`,
          borderRadius: 7, padding: "5px 14px", color: T.navy,
          fontWeight: 700, fontSize: 12, display: "flex", alignItems: "center",
          gap: 5, flexShrink: 0, cursor: "pointer", fontFamily: FONT,
        }}>
          <Upload size={12} /> Choose File
        </button>
      )
    )}
  </div>
);

// Multi-file list row
const MultiFileList = ({ files, onRemove, onAdd, isUploading, config, inputRef }) => (
  <div>
    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 8 }}>
      {files.map((f, i) => (
        <div key={i} style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "8px 12px", background: "rgba(249,115,22,.05)",
          borderRadius: 8, border: `1px solid rgba(249,115,22,.2)`,
        }}>
          <FileText size={14} color={T.orange} />
          <a href={`${DOWNLOAD_BASE}${f.fileName}`} target="_blank" rel="noopener noreferrer"
            style={{
              flex: 1, fontSize: 12, color: "#007bff", textDecoration: "underline",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
            {f.fileName}
          </a>
          <button onClick={() => onRemove(i)} style={{
            background: "none", border: "none", color: T.danger,
            cursor: "pointer", padding: "0 4px", fontWeight: 800, fontSize: 13,
          }}>✕</button>
        </div>
      ))}
    </div>
    <div style={{
      display: "flex", alignItems: "center", gap: 12, padding: "10px 14px",
      background: T.bg, borderRadius: 10,
      border: `1.5px dashed ${T.border}`, fontFamily: FONT,
    }}>
      <div style={{ background: T.light, borderRadius: 8, padding: 8, flexShrink: 0 }}>
        <Upload size={15} color={T.navy} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: T.text }}>{config.label}</div>
        {isUploading ? (
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
            <div style={{
              width: 10, height: 10, border: `2px solid ${T.light}`,
              borderTop: `2px solid ${T.orange}`, borderRadius: "50%",
              animation: "spin 0.7s linear infinite",
            }} />
            <span style={{ fontSize: 11, color: T.orange, fontWeight: 600 }}>Uploading…</span>
          </div>
        ) : (
          <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>{config.hint}</div>
        )}
      </div>
      {!isUploading && (
        <button onClick={onAdd} style={{
          background: "transparent", border: `1.5px solid ${T.border}`,
          borderRadius: 7, padding: "5px 14px", color: T.navy,
          fontWeight: 700, fontSize: 12, display: "flex", alignItems: "center",
          gap: 5, cursor: "pointer", fontFamily: FONT,
        }}>
          <Plus size={12} /> Add File
        </button>
      )}
    </div>
  </div>
);

// ── Step indicator ─────────────────────────────────────────────────────────────
const STEPS = [
  { label: "Basic Info",    icon: Building  },
  { label: "Profile", icon: BookOpen  },
  { label: "Media Uploads",  icon: Image     },
  { label: "Social",   icon: Globe     },
  { label: "Consent",        icon: Check     },
];


const StepBar = ({  current, onStepClick, isStepCompleted }) => (
  <div style={{
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: "18px 32px 0", gap: 0, fontFamily: FONT,
  }}>
    {STEPS.map((s, i) => {
      const done = isStepCompleted(i);
      const active = i === current;
      return (
        <React.Fragment key={i}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 80 }}>
            <div
  onClick={() => onStepClick(i)}
  style={{
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    minWidth: 80,
    cursor: "pointer"
  }}
>
            <div style={{
              width: 34, height: 34, borderRadius: "50%",
              background: done ? T.success : active ? T.orange : T.light,
              display: "flex", alignItems: "center", justifyContent: "center",
              border: `2px solid ${done ? T.success : active ? T.orange : T.border}`,
              transition: "all .2s",
            }}>
              {done
                ? <Check size={15} color="#fff" strokeWidth={3} />
                : <s.icon size={14} color={active ? "#fff" : T.muted} />}
            </div>
            </div>
            <span style={{
              fontSize: 15, fontWeight: 700, color: active ? T.orange : done ? T.success : T.muted,
              marginTop: 4, textAlign: "center", letterSpacing: .3, textTransform: "uppercase",
            }}>{s.label}</span>
          </div>
          {i < STEPS.length - 1 && (
            <div style={{
              flex: 1, height: 2, background: done ? T.success : T.light,
              margin: "-18px 4px 0", transition: "background .3s",
            }} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

// ── Main Component ─────────────────────────────────────────────────────────────
function SchoolRegistration() {
  const uc = getUserCredentials();
  const [step, setStep]                     = useState(0);
  const [existingProfile, setExistingProfile] = useState(null);
  const [saving, setSaving]                 = useState(false);
  const [saveStatus, setSaveStatus]         = useState(null);
  const [loading, setLoading]               = useState(true);
  const [previewMode, setPreviewMode]       = useState(false);

useEffect(() => {
  console.log("step changed");
document.documentElement.scrollTop = 0;
document.body.scrollTop = 0;
}, [step]);

const isStepCompleted = (stepNo) => {
  switch (stepNo) {
    case 0:
      return (
        formData.school_name &&
        formData.affiliation_board &&
        formData.school_type &&
        formData.address_line1 &&
        formData.city &&
        formData.state &&
        formData.pin_code &&
        formData.year_established &&
        formData.phone_number &&
        formData.official_email &&
        formData.principal_name
      );

    case 1:
      return !!formData.school_description;


    case 2:
      return (
          !!logoFile &&
          campusFiles.length > 0
      );

    case 3:
      return true; // optional

    case 4:
      return (
        formData.consent_accuracy &&
        formData.consent_media &&
        formData.consent_communication
      );

    default:
      return false;
  }
};
  // ── Form Data ──────────────────────────────────────────────────────────────
  const [formData, setFormData] = useState({
    // School Information – Basic
    school_name: "", affiliation_board: "", other_board: "",
    year_established: "", school_type: "",
    // Address
    address_line1: "", address_line2: "", city: "",
    state: "", pin_code: "", country: "India",
    // Contact
    phone_number: "", official_email: "", website_url: "",
    principal_name: "", principal_email: "",
    // School Profile
    school_description: "",
    streams_offered: [], grades_available: "", student_strength: "", faculty_strength: "",
    student_exchange: false, intl_collaborations: false,
    university_partnerships: false, overseas_counseling: false,
    // Media
    school_logo: "", campus_photos: "", brochures: "", video_url: "",
    // Social
    instagram_handle: "", facebook_page: "", linkedin_page: "", youtube_channel: "",
    // Consent
    consent_accuracy: false, consent_media: false, consent_communication: false,
  });

  const goToStep = (newStep) => {
  setStep(newStep);

  setTimeout(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, 50);
};


  const validateStep = (currentStep) => {
  switch (currentStep) {
    case 0:
      return (
        formData.school_name &&
        formData.affiliation_board &&
        formData.school_type &&
        formData.address_line1 &&
        formData.city &&
        formData.state &&
        formData.pin_code &&
        formData.phone_number &&
        formData.official_email &&
        formData.principal_name && 
        formData.year_established &&
        formData.website_url && 
        representatives.every(rep =>
            rep.name &&
            rep.email &&
            rep.phone &&
            rep.linkedin
        )
      );

    case 1:
      return formData.school_description;

    case 2:
      return (
          !!logoFile &&
          campusFiles.length > 0
      );
    case 3:
      return true; // social media optional

    case 4:
      return (
        formData.consent_accuracy &&
        formData.consent_media &&
        formData.consent_communication
      );

    default:
      return true;
  }
};

  // Representatives
  const [representatives, setRepresentatives] = useState([
  {
    name:"",
    email:"",
    phone:"",
    linkedin:""
  }
]);

  // ── File uploads ───────────────────────────────────────────────────────────
  const [logoFile, setLogoFile]           = useState(null);
  const [campusFiles, setCampusFiles]     = useState([]);
  const [brochureFiles, setBrochureFiles] = useState([]);
  const [uploadingKeys, setUploadingKeys] = useState({});

  const logoRef     = useRef(null);
  const campusRef   = useRef(null);
  const brochureRef = useRef(null);

  // ── Collapsible sections per step ─────────────────────────────────────────
  const [openSections, setOpenSections] = useState({
    basic: true, address: true, contact: true,
    profile: true, academic: true, intl: true,
    logo: true, campus: true, brochures: true, video: true,
    social: true, consent: true,
  });
  const toggleSection = k => setOpenSections(p => ({ ...p, [k]: !p[k] }));

  // ── Load existing profile ──────────────────────────────────────────────────
  useEffect(() => { fetchExistingProfile(); }, []);

  const fetchExistingProfile = async () => {
    try {
      const creds = Fetchexecutequery();
      const res = await axios.post(creds[1], {
        query: `SELECT * FROM edu_platform.school_profile WHERE division_id = ${uc.division[0]} AND is_active = 1 LIMIT 1;`,
      }, creds[0]);
      if (res.data && res.data.length > 0) {
        const row = res.data[0];
        setExistingProfile(row.primary_id);
        setFormData(prev => ({
          ...prev,
          school_name:            row.school_name            || "",
          affiliation_board:      row.affiliation_board      || "",
          other_board:            row.other_board            || "",
          year_established:       row.year_established       || "",
          school_type:            row.school_type            || "",
          address_line1:          row.address_line1          || "",
          address_line2:          row.address_line2          || "",
          city:                   row.city                   || "",
          state:                  row.state                  || "",
          pin_code:               row.pin_code               || "",
          country:                row.country                || "India",
          phone_number:           row.phone_number           || "",
          official_email:         row.official_email         || "",
          website_url:            row.website_url            || "",
          principal_name:         row.principal_name         || "",
          principal_email:        row.principal_email        || "",
          school_description:     row.school_description     || "",
          streams_offered:        row.streams_offered        ? JSON.parse(row.streams_offered) : [],
          grades_available:       row.grades_available       || "",
          student_strength:       row.student_strength       || "",
          faculty_strength:       row.faculty_strength       || "",
          student_exchange:       !!row.student_exchange,
          intl_collaborations:    !!row.intl_collaborations,
          university_partnerships:!!row.university_partnerships,
          overseas_counseling:    !!row.overseas_counseling,
          school_logo:            row.school_logo            || "",
          campus_photos:          row.campus_photos          || "",
          brochures:              row.brochures              || "",
          video_url:              row.video_url              || "",
          instagram_handle:       row.instagram_handle       || "",
          facebook_page:          row.facebook_page          || "",
          linkedin_page:          row.linkedin_page          || "",
          youtube_channel:        row.youtube_channel        || "",
          consent_accuracy:       !!row.consent_accuracy,
          consent_media:          !!row.consent_media,
          consent_communication:  !!row.consent_communication,
        }));
        if (row.representatives) {
          try { setRepresentatives(JSON.parse(row.representatives)); }
          catch { setRepresentatives([{ name: "", email: "", phone: "" }]); }
        }
        if (row.school_logo) setLogoFile({ fileName: row.school_logo, filePath: row.school_logo });
        if (row.campus_photos) {
          try { setCampusFiles(JSON.parse(row.campus_photos).map(f => ({ fileName: f }))); }
          catch {}
        }
        if (row.brochures) {
          try { setBrochureFiles(JSON.parse(row.brochures).map(f => ({ fileName: f }))); }
          catch {}
        }
      }
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (name, value) =>
    setFormData(p => ({ ...p, [name]: value }));

  // ── Representatives ────────────────────────────────────────────────────────
  const addRep = () =>
  setRepresentatives(p=>[
    ...p,
    {
      name:"",
      email:"",
      phone:"",
      linkedin:""
    }
  ]);
  const removeRep = i => setRepresentatives(p => p.filter((_, idx) => idx !== i));
  const updateRep = (i, key, value) => {
    const r = [...representatives];
    r[i][key] = value;
    setRepresentatives(r);
  };

  // ── File upload ────────────────────────────────────────────────────────────
  const uploadFile = async (key, maxMB, file) => {
    if (!file) return null;
    if (file.size > maxMB * 1024 * 1024) {
      alert(`File too large. Maximum size is ${maxMB} MB.`);
      return null;
    }
    setUploadingKeys(p => ({ ...p, [key]: true }));
    const fd = new FormData();
    fd.append("file", file);
    fd.append("filePath", STATIC_PATH);
    try {
      const res = await fetch(UPLOAD_URL, { method: "POST", body: fd });
      if (res.ok) {
        const data = await res.json();
        return { fileName: data.Filename, filePath: data.Filepath };
      } else {
        const err = await res.json();
        alert("Upload failed: " + (err.Message || "Unknown error"));
        return null;
      }
    } catch (err) {
      console.error("Upload error:", err);
      alert("Upload failed. Please try again.");
      return null;
    } finally {
      setUploadingKeys(p => ({ ...p, [key]: false }));
    }
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const result = await uploadFile("logo", LOGO_CONFIG.maxMB, file);
    if (result) {
      setLogoFile(result);
      handleChange("school_logo", result.fileName);
    }
    if (logoRef.current) logoRef.current.value = "";
  };

  const handleCampusAdd = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      const result = await uploadFile("campus", CAMPUS_CONFIG.maxMB, file);
      if (result) setCampusFiles(p => [...p, result]);
    }
    if (campusRef.current) campusRef.current.value = "";
  };

  const handleBrochureAdd = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      const result = await uploadFile("brochure", BROCHURE_CONFIG.maxMB, file);
      if (result) setBrochureFiles(p => [...p, result]);
    }
    if (brochureRef.current) brochureRef.current.value = "";
  };

  // ── Completion ─────────────────────────────────────────────────────────────
  const requiredFields = [
    "school_name", "affiliation_board", "school_type",
    "address_line1", "city", "state", "pin_code","year_established",
    "phone_number", "official_email", "principal_name",
    "school_description","website_url"
  ];
  const filled = requiredFields.filter(f => {
    const v = formData[f];
    return v && String(v).trim() !== "";
  }).length;
  const completion = Math.round((filled / requiredFields.length) * 100);
  const logoFilled = !!logoFile;
  if (logoFilled) {} // used in completion display
  const consentDone = formData.consent_accuracy && formData.consent_media && formData.consent_communication;
  const missingRequired = requiredFields.filter(f => !formData[f] || String(formData[f]).trim() === "");

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = async (status) => {
    setSaving(true);
    setSaveStatus(null);
    const now = getFormattedDate();
    const payload_data = {
      ...formData,
      streams_offered: JSON.stringify(formData.streams_offered),
      representatives: JSON.stringify(representatives),
      campus_photos:   JSON.stringify(campusFiles.map(f => f.fileName)),
      brochures:       JSON.stringify(brochureFiles.map(f => f.fileName)),
      profile_status:  status,
      modified_date:   now,
      modified_by:     uc.user_id,
      user_id:         uc.user_id,
      is_active:       1,
    };
    try {
      if (existingProfile) {
        const creds = Fetchupdatequery();
        await axios.post(creds[1], {
          schema_name: "edu_platform",
          table: "school_profile",
          set_column: [payload_data],
          where: [{ primary_id: existingProfile }],
        }, creds[0]);
      } else {
        const creds = CreateData();
        await axios.post(creds[1], {
          database: "edu_platform",
          table: "school_profile",
          formData: {
            ...payload_data,
            tenant_id:    uc.tenants_id,
            primart_id:   uc.division[0],
            division_id:  uc.division[0],
            entity_id:    uc.selectedentity || uc.entities_id,
            group_id:     uc.groups_id,
            created_by:   uc.user_id,
            created_date: now,
          },
        }, creds[0]);
        await fetchExistingProfile();
      }
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus(null), 3500);
    } catch (err) {
      console.error("Save error:", err);
      setSaveStatus("error");
      setTimeout(() => setSaveStatus(null), 4000);
    } finally {
      setSaving(false);
    }
  };

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center", color: T.navy }}>
          <div style={{
            width: 44, height: 44, border: `4px solid ${T.light}`,
            borderTop: `4px solid ${T.orange}`, borderRadius: "50%",
            animation: "spin 0.8s linear infinite", margin: "0 auto 12px",
          }} />
          <style>{`
  @keyframes spin { to { transform: rotate(360deg); } }
  * { box-sizing: border-box; }

  input::placeholder,
  textarea::placeholder {
    color: #94a3b8 !important;
    opacity: 1 !important;
  }
`}</style>
          <p style={{ fontWeight: 700, fontSize: 14, margin: 0, fontFamily: FONT }}>Loading…</p>
        </div>
      </div>
    );
  }

  // ── Steps rendering ────────────────────────────────────────────────────────
  const renderStep0 = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {/* Basic Details */}
      <Card>
        <SectionHeader icon={Building} title="Basic Details" isOpen={openSections.basic} onToggle={() => toggleSection("basic")} />
        {openSections.basic && (
          <div style={{padding: 24,display: "grid",gridTemplateColumns: "repeat(3, minmax(250px,1fr))",gap: 16}}>
            <Field label="School Name" required fullWidth>
              <Input value={formData.school_name} onChange={e => handleChange("school_name", e.target.value)} placeholder="Enter full school name" />
            </Field>
            <Field label="Affiliation Board" required>
              <Select value={formData.affiliation_board} onChange={e => handleChange("affiliation_board", e.target.value)}>
                <option value="">Select board</option>
                {AFFILIATION_BOARDS.map(b => <option key={b} value={b}>{b}</option>)}
              </Select>
            </Field>
            {formData.affiliation_board === "Other" && (
              <Field label="Specify Board" required>
                <Input value={formData.other_board} onChange={e => handleChange("other_board", e.target.value)} placeholder="Enter board name" />
              </Field>
            )}
            <Field label="Year Established" required>
              <Input type="number" value={formData.year_established} onChange={e => handleChange("year_established", e.target.value)} placeholder="e.g. 1995" min="1800" max={new Date().getFullYear()} />
            </Field>
            <Field label="Type of School" required>
              <Select value={formData.school_type} onChange={e => handleChange("school_type", e.target.value)}>
                <option value="">Select type</option>
                {SCHOOL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </Select>
            </Field>
          </div>
        )}
      </Card>

      {/* School Address */}
      <Card>
        <SectionHeader icon={Building} title="School Address" isOpen={openSections.address} onToggle={() => toggleSection("address")} />
        {openSections.address && (
          <div style={{padding: 24,display: "grid",gridTemplateColumns: "repeat(3, minmax(250px,1fr))",gap: 16}}>
            <Field label="Address Line 1" required fullWidth>
              <Input value={formData.address_line1} onChange={e => handleChange("address_line1", e.target.value)} placeholder="Building / Street" />
            </Field>
            <Field label="Address Line 2" fullWidth>
              <Input value={formData.address_line2} onChange={e => handleChange("address_line2", e.target.value)} placeholder="Area / Landmark (optional)" />
            </Field>
            <Field label="City" required>
              <Input value={formData.city} onChange={e => handleChange("city", e.target.value)} placeholder="City" />
            </Field>
            <Field label="State" required>
              <Select value={formData.state} onChange={e => handleChange("state", e.target.value)}>
                <option value="">Select state / UT</option>
                {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </Select>
            </Field>
            <Field label="PIN Code" required>
              <Input value={formData.pin_code} onChange={e => handleChange("pin_code", e.target.value)} placeholder="6-digit PIN code" maxLength={6} />
            </Field>
            <Field label="Country">
              <Input value={formData.country} readOnly style={{ background: T.light, cursor: "not-allowed" }} />
            </Field>
          </div>
        )}
      </Card>

      {/* Contact Information */}
      <Card>
        <SectionHeader icon={Users} title="Contact Information" isOpen={openSections.contact} onToggle={() => toggleSection("contact")} />
        {openSections.contact && (
          <div style={{ padding: 24 }}>
            <div style={{display: "grid",gridTemplateColumns: "repeat(3, minmax(250px,1fr))",gap: 16}}>
              <Field label="School Phone Number" required>
                <Input value={formData.phone_number} onChange={e => handleChange("phone_number", e.target.value)} placeholder="+91-XXXXXXXXXX" />
              </Field>
              <Field label="Official Email Address" required>
                <Input type="email" value={formData.official_email} onChange={e => handleChange("official_email", e.target.value)} placeholder="school@example.edu.in" />
              </Field>
              <Field label="Website URL" fullWidth required>
                <Input value={formData.website_url} onChange={e => handleChange("website_url", e.target.value)} placeholder="https://yourschool.edu.in" />
              </Field>
              <Field label="Principal's Name" required>
                <Input value={formData.principal_name} onChange={e => handleChange("principal_name", e.target.value)} placeholder="Full name" />
              </Field>
              <Field label="Principal's Email">
                <Input type="email" value={formData.principal_email} onChange={e => handleChange("principal_email", e.target.value)} placeholder="principal@school.edu.in" />
              </Field>
            </div>

            {/* Representatives */}
            <div style={{ marginTop: 20 }}>
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                marginBottom: 12,
              }}>
                <SubHeader title={`Counselors (${representatives.length})`} />
                <Btn variant="outline" size="sm" icon={UserPlus} onClick={addRep}>Add Counselor</Btn>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr 1fr 40px",
                  gap: 10,
                  marginBottom: 8,
                  padding: "0 13px", // 12px (row padding) + 1px (row border) to align with row content
                  boxSizing: "border-box",
                }}
              >
                <div style={{ fontWeight: 700, color: T.navy }}>Name</div>
                <div style={{ fontWeight: 700, color: T.navy }}>Email</div>
                <div style={{ fontWeight: 700, color: T.navy }}>Mobile</div>
                <div style={{ fontWeight: 700, color: T.navy }}>LinkedIn</div>
                <div></div>
              </div>

                {representatives.map((rep, i) => (
                  <div key={i} style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr 1fr 40px",
                    gap: 10, marginBottom: 10, padding: 12,
                    background: T.bg, borderRadius: 8, border: `1px solid ${T.border}`,
                    boxSizing: "border-box",
                  }}>
                  <Input
                    placeholder="Full Name"
                    value={rep.name}
                    onChange={e=>updateRep(i,"name",e.target.value)}
                  />

                  <Input
                    type="email"
                    placeholder="Email"
                    value={rep.email}
                    onChange={e=>updateRep(i,"email",e.target.value)}
                  />

                  <Input
                    placeholder="Phone"
                    value={rep.phone}
                    onChange={e=>updateRep(i,"phone",e.target.value)}
                  />

                  <Input
                    placeholder="LinkedIn Profile"
                    value={rep.linkedin}
                    onChange={e=>updateRep(i,"linkedin",e.target.value)}
                  />
                  <button
                    onClick={() => removeRep(i)}
                    disabled={representatives.length === 1}
                    style={{
                      width: 40, height: "100%",
                      background: representatives.length === 1 ? T.light : "rgba(220,38,38,.1)",
                      border: "none", borderRadius: 8,
                      cursor: representatives.length === 1 ? "not-allowed" : "pointer",
                      color: T.danger, display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                  >
                    <Minus size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>
    </div>
  );

  const renderStep1 = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {/* About */}
      <Card>
        <SectionHeader icon={BookOpen} title="About the School" isOpen={openSections.profile} onToggle={() => toggleSection("profile")} />
        {openSections.profile && (
          <div style={{ padding: 24 }}>
            <Field label="Short School Description" required hint="Describe your school's mission, values, and highlights in up to 300 words.">
              <Textarea
                value={formData.school_description}
                onChange={e => handleChange("school_description", e.target.value)}
                maxLength={300}
                placeholder="Describe your school's mission, facilities, achievements and what makes it unique…"
                style={{ minHeight: 110 }}
              />
            </Field>
          </div>
        )}
      </Card>

      {/* Academic Highlights */}
      <Card>
        <SectionHeader icon={BookOpen} title="Academic Highlights" isOpen={openSections.academic} onToggle={() => toggleSection("academic")} />
        {openSections.academic && (
          <div style={{ padding: 24, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            <div style={{ gridColumn: "1/-1" }}>
              <Field label="Streams Offered">
                <StreamToggle value={formData.streams_offered} onChange={v => handleChange("streams_offered", v)} />
              </Field>
            </div>
            <Field label="Grades Available" hint="e.g. Grade 1 – 12 or Nursery to Class 12">
              <Input value={formData.grades_available} onChange={e => handleChange("grades_available", e.target.value)} placeholder="e.g. Nursery – Class 12" />
            </Field>
            <Field label="Student Strength">
              <Input type="number" value={formData.student_strength} onChange={e => handleChange("student_strength", e.target.value)} placeholder="Total number of students" />
            </Field>
            <Field label="Faculty Strength">
              <Input type="number" value={formData.faculty_strength} onChange={e => handleChange("faculty_strength", e.target.value)} placeholder="Total number of faculty" />
            </Field>
          </div>
        )}
      </Card>

      {/* International Engagement */}
      <Card>
        <SectionHeader icon={Globe} title="International Engagement" isOpen={openSections.intl} onToggle={() => toggleSection("intl")} />
        {openSections.intl && (
          <div style={{ padding: 24 }}>
            {[
              { key: "student_exchange",         label: "Student Exchange Programs",     desc: "Existing or planned international student exchange programs" },
              { key: "intl_collaborations",       label: "International Collaborations",  desc: "Formal partnerships with international schools or organisations" },
              { key: "university_partnerships",   label: "University Partnerships",       desc: "Agreements with universities for guided admission pathways" },
              { key: "overseas_counseling",       label: "Overseas Counseling Support",   desc: "Dedicated counseling for students seeking education abroad" },
            ].map(item => (
              <div key={item.key} style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "12px 16px", marginBottom: 10,
                background: formData[item.key] ? "rgba(249,115,22,.05)" : T.bg,
                borderRadius: 10,
                border: `1.5px solid ${formData[item.key] ? "rgba(249,115,22,.25)" : T.border}`,
              }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{item.label}</div>
                  <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>{item.desc}</div>
                </div>
                <ToggleSwitch value={formData[item.key]} onChange={v => handleChange(item.key, v)} />
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );

  const renderStep2 = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {/* Logo */}
      <Card>
        <SectionHeader
          icon={Image}
          title="School Branding (Required)"
          isOpen={openSections.logo}
          onToggle={() => toggleSection("logo")}
        />
        {openSections.logo && (
          <div style={{ padding: 20 }}>
            <input ref={logoRef} type="file" accept={LOGO_CONFIG.accept} style={{ display: "none" }} onChange={handleLogoChange} />
            <SingleFileRow
              config={LOGO_CONFIG}
              uploaded={logoFile}
              isUploading={!!uploadingKeys.logo}
              onChoose={() => logoRef.current?.click()}
              onClear={() => { setLogoFile(null); handleChange("school_logo", ""); }}
            />
          </div>
        )}
      </Card>

      {/* Campus Photos */}
      <Card>
        <SectionHeader icon={Image} title="Campus Images" badge={campusFiles.length || undefined} isOpen={openSections.campus} onToggle={() => toggleSection("campus")} />
        {openSections.campus && (
          <div style={{ padding: 20 }}>
            <input ref={campusRef} type="file" accept={CAMPUS_CONFIG.accept} multiple style={{ display: "none" }} onChange={handleCampusAdd} />
            <MultiFileList
              config={CAMPUS_CONFIG}
              files={campusFiles}
              isUploading={!!uploadingKeys.campus}
              onAdd={() => campusRef.current?.click()}
              onRemove={i => setCampusFiles(p => p.filter((_, idx) => idx !== i))}
              inputRef={campusRef}
            />
          </div>
        )}
      </Card>

      {/* Brochures */}
      <Card>
        <SectionHeader icon={FileText} title="Promotional Materials" badge={brochureFiles.length || undefined} isOpen={openSections.brochures} onToggle={() => toggleSection("brochures")} />
        {openSections.brochures && (
          <div style={{ padding: 20 }}>
            <input ref={brochureRef} type="file" accept={BROCHURE_CONFIG.accept} multiple style={{ display: "none" }} onChange={handleBrochureAdd} />
            <MultiFileList
              config={BROCHURE_CONFIG}
              files={brochureFiles}
              isUploading={!!uploadingKeys.brochure}
              onAdd={() => brochureRef.current?.click()}
              onRemove={i => setBrochureFiles(p => p.filter((_, idx) => idx !== i))}
              inputRef={brochureRef}
            />
          </div>
        )}
      </Card>

      {/* Video Links */}
      <Card>
        <SectionHeader icon={Video} title="School Video Links" isOpen={openSections.video} onToggle={() => toggleSection("video")} />
        {openSections.video && (
          <div style={{ padding: 20 }}>
            <Field label="YouTube / Video URL" hint="Link to a school tour, introduction video, or event highlight reel">
              <div style={{ display: "flex" }}>
                <div style={{
                  background: T.light, border: `1.5px solid ${T.border}`, borderRight: "none",
                  borderRadius: "8px 0 0 8px", padding: "9px 12px",
                  display: "flex", alignItems: "center",
                }}>
                  <Video size={14} color={T.muted} />
                </div>
                <Input
                  value={formData.video_url}
                  onChange={e => handleChange("video_url", e.target.value)}
                  placeholder="https://youtube.com/watch?v=..."
                  style={{ borderRadius: "0 8px 8px 0" }}
                />
              </div>
            </Field>
          </div>
        )}
      </Card>
    </div>
  );

  const renderStep3 = () => (
    <Card>
      <SectionHeader icon={Globe} title="Social Media Presence" isOpen={openSections.social} onToggle={() => toggleSection("social")} />
      {openSections.social && (
        <div style={{ padding: 24 }}>
          {[
            { key: "instagram_handle", label: "Instagram Handle",  icon: Globe, placeholder: "@yourschool or https://instagram.com/..." },
            { key: "facebook_page",    label: "Facebook Page",     icon: Link,  placeholder: "https://facebook.com/yourschool" },
            { key: "linkedin_page",    label: "LinkedIn Page",     icon: Link,  placeholder: "https://linkedin.com/school/..." },
            { key: "youtube_channel",  label: "YouTube Channel",   icon: Video, placeholder: "https://youtube.com/@yourschool" },
          ].map(s => (
            <Field key={s.key} label={s.label} style={{ marginBottom: 16 }}>
              <div style={{ display: "flex" }}>
                <div style={{
                  background: T.light, border: `1.5px solid ${T.border}`, borderRight: "none",
                  borderRadius: "8px 0 0 8px", padding: "9px 12px",
                  display: "flex", alignItems: "center",
                }}>
                  <s.icon size={14} color={T.muted} />
                </div>
                <Input
                  value={formData[s.key]}
                  onChange={e => handleChange(s.key, e.target.value)}
                  placeholder={s.placeholder}
                  style={{ borderRadius: "0 8px 8px 0" }}
                />
              </div>
            </Field>
          ))}
        </div>
      )}
    </Card>
  );

  const renderStep4 = () => (
    <Card>
      <SectionHeader icon={Check} title="Consent & Declaration" isOpen={openSections.consent} onToggle={() => toggleSection("consent")} />
      {openSections.consent && (
        <div style={{ padding: 24 }}>
          <div style={{
            background: "rgba(249,115,22,.06)", borderRadius: 10,
            border: `1px solid rgba(249,115,22,.2)`, padding: "16px 20px",
            marginBottom: 24,
          }}>
            <p style={{ fontSize: 13, color: T.text, margin: 0, lineHeight: 1.7, fontFamily: FONT }}>
              Please read each declaration carefully before confirming your submission. All three consents are required to complete registration.
            </p>
          </div>
          <Checkbox
            checked={formData.consent_accuracy}
            onChange={v => handleChange("consent_accuracy", v)}
            required
            label="We confirm that all information provided in this registration form is accurate, complete, and authorised by the institution."
          />
          <Checkbox
            checked={formData.consent_media}
            onChange={v => handleChange("consent_media", v)}
            required
            label="We consent to the use of uploaded media, logos, and event information for promotional, collaboration, and platform-listing purposes."
          />
          <Checkbox
            checked={formData.consent_communication}
            onChange={v => handleChange("consent_communication", v)}
            required
            label="We agree to receive communication from EduPlatform regarding partnerships, student engagement events, and relevant educational opportunities."
          />
          {!consentDone && (
            <div style={{
              display: "flex", alignItems: "center", gap: 8,
              background: "rgba(220,38,38,.07)", borderRadius: 8,
              padding: "10px 14px", marginTop: 16,
            }}>
              <AlertCircle size={15} color={T.danger} />
              <span style={{ fontSize: 12, color: T.danger, fontWeight: 600, fontFamily: FONT }}>
                All three declarations must be accepted to submit registration.
              </span>
            </div>
          )}
          {consentDone && (
            <div style={{
              display: "flex", alignItems: "center", gap: 8,
              background: "rgba(22,163,74,.1)", borderRadius: 8,
              padding: "10px 14px", marginTop: 16,
            }}>
              <CheckCircle size={15} color={T.success} />
              <span style={{ fontSize: 12, color: T.success, fontWeight: 700, fontFamily: FONT }}>
                All declarations accepted. You're ready to submit.
              </span>
            </div>
          )}
        </div>
      )}
    </Card>
  );

  // ── Preview Modal ──────────────────────────────────────────────────────────
  const PreviewModal = () => (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(6,21,53,.55)",
      zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center",
      padding: 24,
    }}>
      <div style={{
        background: T.card, borderRadius: 16, maxWidth: 640,
        width: "100%", maxHeight: "85vh", overflow: "auto",
        boxShadow: "0 24px 60px rgba(6,21,53,.3)",
        fontFamily: FONT,
      }}>
        <div style={{
          background: `linear-gradient(135deg, ${T.navy}, ${T.navyMid})`,
          padding: "18px 24px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          position: "sticky", top: 0,
        }}>
          <span style={{ color: "#fff", fontWeight: 800, fontSize: 15 }}>Submission Preview</span>
          <button onClick={() => setPreviewMode(false)} style={{
            background: "rgba(255,255,255,.15)", border: "none", borderRadius: 6,
            padding: "4px 12px", color: "#fff", cursor: "pointer", fontWeight: 700, fontSize: 12,
          }}>Close</button>
        </div>
        <div style={{ padding: 24 }}>
          {[
            ["School Name", formData.school_name],
            ["Affiliation Board", formData.affiliation_board === "Other" ? formData.other_board : formData.affiliation_board],
            ["School Type", formData.school_type],
            ["Year Established", formData.year_established],
            ["Address", [formData.address_line1, formData.address_line2, formData.city, formData.state, formData.pin_code].filter(Boolean).join(", ")],
            ["Phone", formData.phone_number],
            ["Email", formData.official_email],
            ["Website", formData.website_url],
            ["Principal", formData.principal_name],
            ["Streams Offered", formData.streams_offered.join(", ") || "—"],
            ["Student Strength", formData.student_strength || "—"],
            ["Faculty Strength", formData.faculty_strength || "—"],
            ["School Logo", formData.school_logo || "Not uploaded"],
            ["Campus Photos", campusFiles.length > 0 ? `${campusFiles.length} file(s)` : "None"],
            ["Brochures", brochureFiles.length > 0 ? `${brochureFiles.length} file(s)` : "None"],
            ["Video URL", formData.video_url || "—"],
            ["Instagram", formData.instagram_handle || "—"],
            ["Consent Given", consentDone ? "Yes" : "No"],
          ].map(([k, v]) => (
            <div key={k} style={{
              display: "flex", padding: "9px 0",
              borderBottom: `1px solid ${T.light}`,
            }}>
              <span style={{ width: 160, fontSize: 12, color: T.muted, fontWeight: 700, flexShrink: 0 }}>{k}</span>
              <span style={{ fontSize: 13, color: T.text, flex: 1 }}>{v || "—"}</span>
            </div>
          ))}
          <div style={{ marginTop: 20, display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <Btn variant="secondary" onClick={() => setPreviewMode(false)}>Go Back & Edit</Btn>
            <Btn variant="primary" icon={Check} onClick={() => { setPreviewMode(false); handleSave("submitted"); }} disabled={!consentDone || saving}>
              {saving ? "Submitting…" : "Confirm & Submit"}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );

  // ── Full render ────────────────────────────────────────────────────────────
// ── Full render ────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: "100vh", background: T.bg, fontFamily: FONT }}>
      <style>{`
  @keyframes spin { to { transform: rotate(360deg); } }
  * { box-sizing: border-box; }

  input::placeholder,
  textarea::placeholder {
    color: #94a3b8 !important;
    opacity: 1 !important;
  }
`}</style>

      {previewMode && <PreviewModal />}

      <h2 className="pt-1 pb-1 text-center" style={{ color: T.navy, fontSize: "18px", margin: 0, padding: "16px 0 0" }}>
        SCHOOL REGISTRATION
      </h2>

      <div style={{ width: "100%", padding: "24px 24px 48px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 300px", gap: 24, alignItems: "start" }}>

          {/* ── Form area ── */}
          <div>
            <StepBar
              current={step}
              onStepClick={(clickedStep) => {
                if (clickedStep <= step || validateStep(step)) {
                  setStep(clickedStep);
                  window.scrollTo({
                    top: 0,
                    behavior: "smooth"
                  });
                }
              }}
              isStepCompleted={isStepCompleted}
            />

            <div style={{ marginTop: 18 }}>
              {step === 0 && renderStep0()}
              {step === 1 && renderStep1()}
              {step === 2 && renderStep2()}
              {step === 3 && renderStep3()}
              {step === 4 && renderStep4()}
            </div>

            {/* Navigation */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              marginTop: 24, paddingBottom: 8,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {step > 0 && (
                  <Btn variant="outline" onClick={() => goToStep(step - 1)}>← Back</Btn>
                )}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {saveStatus === "saved" && (
                  <span style={{ display: "flex", alignItems: "center", gap: 6, color: T.success, fontWeight: 700, fontSize: 13 }}>
                    <CheckCircle size={15} /> Saved
                  </span>
                )}
                {saveStatus === "error" && (
                  <span style={{ display: "flex", alignItems: "center", gap: 6, color: T.danger, fontWeight: 700, fontSize: 13 }}>
                    <AlertCircle size={15} /> Save failed
                  </span>
                )}
                <Btn variant="secondary" icon={Save} onClick={() => handleSave("draft")} disabled={saving}>
                  Save Draft
                </Btn>
                {step < STEPS.length - 1 ? (
                  <Btn
                    variant="primary"
                    disabled={!validateStep(step)}
                    onClick={() => setStep(s => s + 1)}
                  >
                    Next →
                  </Btn>

                ) : (
                  <>
                    <Btn variant="navy" icon={Eye} onClick={() => setPreviewMode(true)}>
                      Preview
                    </Btn>
                    <Btn
                      variant="success"
                      icon={Check}
                      onClick={() => handleSave("submitted")}
                      disabled={saving || !consentDone}
                    >
                      {saving ? "Submitting…" : existingProfile ? "Update Registration" : "Submit Registration"}
                    </Btn>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* ── Sidebar ── */}
          <div style={{ position: "sticky", top: 24 }}>
            <Card style={{ padding: 20, marginBottom: 18 }}>
              <div style={{
                fontWeight: 800, fontSize: 13, color: T.navy, marginBottom: 14,
                textTransform: "uppercase", letterSpacing: .5,
              }}>
                Profile Completion
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 14 }}>
                <DonutChart pct={completion} />
              </div>
              <ProgressBar value={completion} color={completion > 80 ? T.success : T.orange} />

              {missingRequired.length > 0 && (
                <div style={{ marginTop: 18 }}>
                  <div style={{ fontSize: 11, color: T.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: .5, marginBottom: 8 }}>
                    Required Fields Missing
                  </div>
                  {missingRequired.map(f => (
                    <div key={f} style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 7 }}>
                      <div style={{ width: 6, height: 6, borderRadius: "50%", background: T.danger, flexShrink: 0 }} />
                      <span style={{ fontSize: 12, color: T.text }}>
                        {f.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {missingRequired.length === 0 && (
                <div style={{
                  marginTop: 14, display: "flex", alignItems: "center", gap: 6,
                  background: "rgba(22,163,74,.1)", borderRadius: 8, padding: "8px 12px",
                }}>
                  <CheckCircle size={14} color={T.success} />
                  <span style={{ fontSize: 12, color: T.success, fontWeight: 700 }}>
                    All required fields filled
                  </span>
                </div>
              )}

              <div style={{ marginTop: 18, borderTop: `1px solid ${T.border}`, paddingTop: 14 }}>
                <div style={{ fontSize: 11, color: T.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: .5, marginBottom: 8 }}>
                  Status
                </div>
                <div style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  background: existingProfile ? "rgba(249,115,22,.1)" : T.light,
                  borderRadius: 8, padding: "6px 12px",
                }}>
                  <div style={{ width: 7, height: 7, borderRadius: "50%", background: existingProfile ? T.orange : T.muted }} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: existingProfile ? T.orange : T.muted }}>
                    {existingProfile ? "Profile Exists" : "Not Yet Saved"}
                  </span>
                </div>
              </div>

              {/* Consent status */}
              <div style={{ marginTop: 14, borderTop: `1px solid ${T.border}`, paddingTop: 14 }}>
                <div style={{ fontSize: 11, color: T.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: .5, marginBottom: 8 }}>
                  Consent
                </div>
                {[
                  ["Accuracy", formData.consent_accuracy],
                  ["Media Use", formData.consent_media],
                  ["Communication", formData.consent_communication],
                ].map(([label, val]) => (
                  <div key={label} style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 7 }}>
                    <div style={{ width: 7, height: 7, borderRadius: "50%", background: val ? T.success : T.border, flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: val ? T.success : T.muted, fontWeight: val ? 700 : 400 }}>{label}</span>
                  </div>
                ))}
              </div>
            </Card>

            {/* Step guide */}
            <Card style={{ padding: 16 }}>
              <div style={{ fontSize: 11, color: T.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: .5, marginBottom: 10 }}>
                Steps
              </div>
              {STEPS.map((s, i) => (
                <div
                  key={i}
                  onClick={() => {if (i <= step || validateStep(step)) {setStep(i);}}}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "8px 10px", borderRadius: 8, marginBottom: 4,
                    cursor: "pointer",
                    background: step === i ? "rgba(249,115,22,.1)" : "transparent",
                    border: `1px solid ${step === i ? "rgba(249,115,22,.3)" : "transparent"}`,
                    transition: "all .15s",
                  }}
                >
                  <div style={{
                    width: 24, height: 24, borderRadius: "50%", flexShrink: 0,
                    background: i < step ? T.success : step === i ? T.orange : T.light,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {isStepCompleted(i)
                      ? <Check size={11} color="#fff" strokeWidth={3} />
                      : <s.icon size={11} color={step === i ? "#fff" : T.muted} />}
                  </div>
                  <span style={{
                    fontSize: 12, fontWeight: step === i ? 700 : 500,
                    color: step === i ? T.orange : i < step ? T.success : T.muted,
                  }}>{s.label}</span>
                </div>
              ))}
            </Card>
          </div>

        </div>
      </div>
    </div>
  );
}

export default SchoolRegistration;